import { Search, Download, Upload, PlusSquare, ListPlus, FileUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const InventoryToolbar = ({ searchTerm, onSearchChange, canEdit, canDownload, onExportCSV, onExportLowStockCSV, onExportPDF, onExportExcel, onImportExcel, importDialog, onImportDialogChange, importFile, onImportFileChange, onImportCSV, onBulkAdd, onAddItem, onBulkUpload, onBulkDelete, totalItems }) => {
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>Inventory</h1>
          {totalItems > 0 && <p className="text-slate-500 mt-1 text-sm">{totalItems.toLocaleString()} items total</p>}
        </div>
        <div className="flex gap-2 flex-wrap justify-start sm:justify-end">
          {canDownload && (
            <>
              <Button data-testid="export-excel-button" variant="outline" onClick={onExportExcel} className="border-2 border-green-500 text-green-600 hover:bg-green-50 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                <Download size={16} className="mr-1" /> Excel
              </Button>
              <Button data-testid="export-csv-button" variant="outline" onClick={onExportCSV} className="border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                <Download size={16} className="mr-1" /> CSV
              </Button>
              <Button data-testid="export-pdf-button" variant="outline" onClick={onExportPDF} className="border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                <Download size={16} className="mr-1" /> PDF
              </Button>
            </>
          )}
          {canEdit && (
            <>
              <Dialog open={importDialog} onOpenChange={onImportDialogChange}>
                <DialogTrigger asChild>
                  <Button data-testid="import-csv-button" variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-50 hover:border-orange-600 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                    <Upload size={16} className="mr-1" /> Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 rounded-sm">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Import Inventory</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="uppercase-label text-xs text-slate-600 font-bold">CSV or Excel File</Label>
                      <Input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => onImportFileChange(e.target.files[0])} className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
                      <p className="text-xs text-slate-500 mt-2">CSV/Excel should have columns: Part Number, Name, Brand, Category, Location, Quantity, Reorder Level</p>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button onClick={() => { const f = importFile; if (f?.name?.endsWith('.xlsx') || f?.name?.endsWith('.xls')) { onImportExcel && onImportExcel(); } else { onImportCSV(); } }} data-testid="submit-import-button" className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm btn-snap uppercase font-bold">Import</Button>
                      <Button type="button" variant="outline" onClick={() => onImportDialogChange(false)} className="flex-1 border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold">Cancel</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              {onBulkUpload && (
                <Button data-testid="bulk-upload-button" variant="outline" onClick={onBulkUpload} className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                  <FileUp size={16} className="mr-1" /> Bulk Upload
                </Button>
              )}
              <Button data-testid="bulk-add-button" onClick={onBulkAdd} className="bg-orange-600 text-white hover:bg-orange-700 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                <ListPlus size={16} className="mr-1" /> Bulk Add
              </Button>
              <Button data-testid="add-inventory-button" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm btn-snap uppercase font-bold tracking-wider text-xs" onClick={onAddItem}>
                <PlusSquare size={16} className="mr-1" /> Add Item
              </Button>
              {onBulkDelete && (
                <Button data-testid="bulk-delete-button" variant="destructive" onClick={onBulkDelete} className="rounded-sm btn-snap uppercase font-bold tracking-wider text-xs">
                  <Trash2 size={16} className="mr-1" /> Delete All
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <Input data-testid="inventory-search-input" placeholder="Search by name or Part Number..." value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} className="pl-10 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-white" />
        </div>
      </div>
    </>
  );
};

export { InventoryToolbar };
